import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { EnvelopeSimple, Phone, Buildings, ArrowClockwise, Tray } from "@phosphor-icons/react";

// Website enquiries — a view over records the public website already writes into
// the Hub (Leads, website vacancy submissions, contact form). Nothing is duplicated.
const KINDS = [
  { value: "all", label: "All" },
  { value: "employer", label: "Employer enquiries" },
  { value: "vacancy", label: "Vacancy submissions" },
  { value: "partnership", label: "Partnership" },
  { value: "contact", label: "Contact form" },
];
const KIND_BADGE = {
  employer: "bg-blue-100 text-blue-800", vacancy: "bg-purple-100 text-purple-800",
  partnership: "bg-amber-100 text-amber-800", contact: "bg-slate-100 text-slate-700",
};
const STATUS_BADGE = {
  new: "bg-red-100 text-red-700", contacted: "bg-amber-100 text-amber-800", qualified: "bg-blue-100 text-blue-800",
  converted: "bg-green-100 text-green-800", closed: "bg-gray-100 text-gray-600",
};
const fmt = (iso) => (iso ? new Date(iso).toLocaleString("en-GB", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "");

export default function EnquiriesPage() {
  const [data, setData] = useState({ items: [], counts: {}, statuses: [] });
  const [kind, setKind] = useState("all");
  const [status, setStatus] = useState("");
  const [search, setSearch] = useState("");
  const [members, setMembers] = useState([]);
  const [open, setOpen] = useState(null);
  const [note, setNote] = useState("");
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const p = new URLSearchParams({ kind });
      if (status) p.set("status", status);
      if (search) p.set("search", search);
      const { data } = await api.get(`/enquiries?${p}`);
      setData(data);
      setOpen(o => (o ? data.items.find(i => i.id === o.id) || null : null));
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to load enquiries"); }
    setLoading(false);
  }, [kind, status, search]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/leads/team/members").then(({ data }) => setMembers(data.members || data || [])).catch(() => {}); }, []);

  const update = async (item, patch) => {
    try {
      await api.patch(`/enquiries/${item.record_type}/${item.id}`, patch);
      toast.success("Enquiry updated");
      setNote("");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Update failed"); }
  };

  const statuses = data.statuses?.length ? data.statuses : ["new", "contacted", "qualified", "converted", "closed"];

  return (
    <div className="space-y-4" data-testid="enquiries-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-[#0F172A] dark:text-white flex items-center gap-2"><Tray size={24} /> Website Enquiries</h1>
          <p className="text-sm text-[#6B7280]">Everything submitted through banksmorgan.com — employer enquiries, vacancy submissions, partnership requests and contact-form messages.</p>
        </div>
        <button onClick={load} className="text-sm text-[#6B7280] flex items-center gap-1 hover:text-[#0F172A]"><ArrowClockwise size={16} /> Refresh</button>
      </div>

      <div className="flex flex-wrap gap-2 items-center">
        {KINDS.map(k => (
          <button key={k.value} onClick={() => setKind(k.value)} data-testid={`enquiry-kind-${k.value}`}
            className={`px-3 py-1.5 rounded-full text-xs font-semibold border ${kind === k.value ? "bg-[#0F172A] text-white border-[#0F172A]" : "bg-white text-[#374151] border-[#E5E7EB]"}`}>{k.label}</button>
        ))}
        <select value={status} onChange={e => setStatus(e.target.value)} className="border rounded-lg px-2 py-1.5 text-xs" data-testid="enquiry-status-filter">
          <option value="">All statuses</option>
          {statuses.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
        <Input placeholder="Search company, name, email, phone, message…" value={search} onChange={e => setSearch(e.target.value)} className="max-w-xs h-8 text-xs" />
        <div className="flex gap-2 ml-auto text-xs">
          {statuses.map(s => <span key={s} className={`px-2 py-1 rounded ${STATUS_BADGE[s] || "bg-gray-100"}`}>{s}: {data.counts?.[s] || 0}</span>)}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
        <div className="lg:col-span-3 bg-white dark:bg-[#1E293B] rounded-lg border border-[#E5E7EB] dark:border-[#334155] divide-y divide-[#F3F4F6] dark:divide-[#334155]">
          {loading && <div className="p-6 text-sm text-[#9CA3AF]">Loading…</div>}
          {!loading && data.items.length === 0 && <div className="p-6 text-sm text-[#9CA3AF]">No enquiries match these filters.</div>}
          {data.items.map(i => (
            <button key={`${i.record_type}-${i.id}`} onClick={() => setOpen(i)} data-testid={`enquiry-row-${i.id}`}
              className={`w-full text-left p-3 hover:bg-[#F9FAFB] dark:hover:bg-[#0F172A] ${open?.id === i.id ? "bg-[#FFFBEB] dark:bg-[#0F172A]" : ""}`}>
              <div className="flex items-center gap-2">
                <span className={`px-2 py-0.5 rounded text-[10px] font-semibold ${KIND_BADGE[i.kind]}`}>{KINDS.find(k => k.value === i.kind)?.label || i.kind}</span>
                <span className={`px-2 py-0.5 rounded text-[10px] font-semibold ${STATUS_BADGE[i.status] || "bg-gray-100"}`}>{i.status}</span>
                <span className="text-[11px] text-[#9CA3AF] ml-auto">{fmt(i.created_at)}</span>
              </div>
              <div className="mt-1 text-sm font-semibold text-[#0F172A] dark:text-white">{i.company_name || i.contact_name || i.email}{i.job_title ? ` — ${i.job_title}` : ""}</div>
              <div className="text-xs text-[#6B7280] truncate">{[i.contact_name, i.email, i.phone].filter(Boolean).join(" · ")}</div>
              {i.message && <div className="text-xs text-[#9CA3AF] truncate mt-0.5">{i.message}</div>}
              {i.assigned_to_name && <div className="text-[11px] text-[#C5A059] mt-0.5">Assigned: {i.assigned_to_name}</div>}
            </button>
          ))}
        </div>

        <div className="lg:col-span-2">
          {!open ? (
            <div className="bg-white dark:bg-[#1E293B] rounded-lg border border-dashed border-[#E5E7EB] p-6 text-sm text-[#9CA3AF]">Select an enquiry to see the full details and history.</div>
          ) : (
            <div className="bg-white dark:bg-[#1E293B] rounded-lg border border-[#E5E7EB] dark:border-[#334155] p-4 space-y-3 sticky top-4" data-testid="enquiry-detail">
              <div>
                <div className="text-lg font-bold text-[#0F172A] dark:text-white">{open.company_name || open.contact_name}</div>
                <div className="text-xs text-[#6B7280]">{open.source} · received {fmt(open.created_at)}</div>
              </div>
              <div className="grid grid-cols-2 gap-2 text-sm">
                {[["Employer / company", open.company_name], ["Contact name", open.contact_name], ["Enquiry type", open.enquiry_type], ["Sector", open.sector],
                  ["Job title", open.job_title], ["Location", open.location], ["Salary", open.salary], ["Vacancy status", open.vacancy_status],
                  ...Object.entries(open.extra || {}).map(([k, v]) => [k.replace(/_/g, " "), v])].filter(([, v]) => v).map(([k, v]) => (
                  <div key={k}><div className="text-[10px] uppercase tracking-wider text-[#9CA3AF]">{k}</div><div className="font-medium text-[#0F172A] dark:text-white capitalize-first">{String(v)}</div></div>
                ))}
              </div>
              <div className="flex flex-wrap gap-3 text-sm">
                {open.email && <a href={`mailto:${open.email}`} className="flex items-center gap-1 text-blue-700 hover:underline"><EnvelopeSimple size={14} />{open.email}</a>}
                {open.phone && <a href={`tel:${open.phone}`} className="flex items-center gap-1 text-blue-700 hover:underline"><Phone size={14} />{open.phone}</a>}
              </div>
              {open.message && <div><div className="text-[10px] uppercase tracking-wider text-[#9CA3AF] mb-1">Message</div><div className="text-sm whitespace-pre-wrap bg-[#F9FAFB] dark:bg-[#0F172A] rounded p-2">{open.message}</div></div>}
              <div className="flex flex-wrap gap-2 text-xs">
                {open.company_id && <Link to="/hub/companies" className="flex items-center gap-1 text-[#C5A059] hover:underline"><Buildings size={14} /> Company record created in Companies</Link>}
                {open.vacancy_id && <Link to="/hub/vacancies" className="text-[#C5A059] hover:underline">Open in Vacancies (status: {open.vacancy_status})</Link>}
                {open.record_type === "lead" && <Link to="/hub/leads" className="text-[#C5A059] hover:underline">Open in Leads</Link>}
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-[#9CA3AF] mb-1">Status</div>
                  <select value={open.status} onChange={e => update(open, { status: e.target.value })} className="w-full border rounded-lg px-2 py-1.5 text-sm" data-testid="enquiry-status-select">
                    {[...new Set([open.status, ...statuses])].map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-[#9CA3AF] mb-1">Assigned to</div>
                  <select value={open.assigned_to || ""} onChange={e => update(open, { assigned_to: e.target.value })} className="w-full border rounded-lg px-2 py-1.5 text-sm" data-testid="enquiry-assign-select">
                    <option value="">Unassigned</option>
                    {members.map(m => <option key={m.id || m._id} value={m.id || m._id}>{m.name}</option>)}
                  </select>
                </div>
              </div>
              <div className="flex gap-2">
                <Input placeholder="Add a note to the history…" value={note} onChange={e => setNote(e.target.value)} className="h-8 text-xs" />
                <button disabled={!note} onClick={() => update(open, { note })} className="px-3 text-xs font-semibold rounded bg-[#0F172A] text-white disabled:opacity-40">Add</button>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-wider text-[#9CA3AF] mb-1">History</div>
                <div className="space-y-1 max-h-48 overflow-y-auto">
                  <div className="text-xs text-[#6B7280]">{fmt(open.created_at)} — received via {open.source}</div>
                  {(open.history || []).map((h, idx) => <div key={idx} className="text-xs text-[#374151] dark:text-[#CBD5E1]">{fmt(h.at)} — {h.by_name}: {h.change}</div>)}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
